import type { IntlShape, MessageDescriptor } from "react-intl";
import { formatDate } from "@/lib/format-date";
import {
  ListingApplicationState,
  MatchAutoRunReason,
  MatchAutoRunStatus,
  type MatchAutoMatchStateFragment,
  type MatchAutoRunRowFragment,
} from "@/graphql/generated/operations";

// Was die Oberfläche über die automatischen Läufe eines Matches weiß
// (@external-adr GENESIS-0086-a-listing-ranking-belongs-to-an-immutable-match-version). Reine Ableitungen, damit
// Rangliste, Laufliste und Tests dieselbe Lesart haben; genesis bleibt die einzige Quelle für Zustand und
// Wiederholbarkeit (`retryable`), hier wird nichts davon nachgerechnet.

export type AutoRun = MatchAutoRunRowFragment;

/** "Eingeschaltet von … am …", in den Einstellungen wie über der Laufliste. */
export function enabledByText(intl: IntlShape, autoMatch: NonNullable<MatchAutoMatchStateFragment["autoMatch"]>): string {
  return intl.formatMessage(
    { id: "match.autoMatch.enabledBy", defaultMessage: "Eingeschaltet von {name} am {date}" },
    {
      // genesis liefert die Person ohne Namen oder gar nicht, wenn die lesende Person sie nicht sehen darf.
      name:
        autoMatch.enabledBy?.formattedName ||
        intl.formatMessage({ id: "match.autoMatch.unknownPerson", defaultMessage: "einer unbekannten Person" }),
      date: formatDate(autoMatch.enabledAt, intl.locale),
    },
  );
}

const WITHDRAWN: MessageDescriptor = { id: "match.autoMatch.status.withdrawn", defaultMessage: "Bewerbung zurückgezogen" };

/** Läuft noch und bewegt sich: nur dann lohnt sanftes Nachladen. Ein hängender Lauf bewegt sich nicht. */
export function isInFlight(run: Pick<AutoRun, "status" | "stalled">): boolean {
  return (run.status === MatchAutoRunStatus.Pending || run.status === MatchAutoRunStatus.Running) && !run.stalled;
}

/**
 * Wie ein Lauf in der Liste heißt. Ein hängender Lauf heißt "Hängt", egal ob er wartet oder läuft:
 * für die lesende Person ist beides dasselbe, und nur so ist "Erneut versuchen" daneben verständlich.
 */
export function runStatusLabel(run: Pick<AutoRun, "status" | "stalled" | "copied">): MessageDescriptor {
  if (run.stalled) return { id: "match.autoMatch.status.stalled", defaultMessage: "Hängt" };
  switch (run.status) {
    case MatchAutoRunStatus.Pending:
      return { id: "match.autoMatch.status.pending", defaultMessage: "Wartet" };
    case MatchAutoRunStatus.Running:
      return { id: "match.autoMatch.status.running", defaultMessage: "Läuft" };
    case MatchAutoRunStatus.Done:
      return run.copied
        ? { id: "match.autoMatch.status.copied", defaultMessage: "Im Ergebnis ergänzt" }
        : { id: "match.autoMatch.status.done", defaultMessage: "Bewertet" };
    case MatchAutoRunStatus.Failed:
      return { id: "match.autoMatch.status.failed", defaultMessage: "Fehlgeschlagen" };
    case MatchAutoRunStatus.Skipped:
      return { id: "match.autoMatch.status.skipped", defaultMessage: "Übersprungen" };
    case MatchAutoRunStatus.Withdrawn:
      return WITHDRAWN;
    case MatchAutoRunStatus.DesignChanged:
      return {
        id: "match.autoMatch.status.designChanged",
        defaultMessage: "Anforderungen geändert: neu matchen",
      };
    default:
      return { id: "match.autoMatch.status.unknown", defaultMessage: "Unbekannter Zustand" };
  }
}

// genesis hängt neue Gründe an, ohne alte umzubenennen; ein Grund, den dieser Build nicht kennt, fällt
// still weg, statt einen falschen Text zu zeigen.
const REASON_LABELS: Record<MatchAutoRunReason, MessageDescriptor> = {
  [MatchAutoRunReason.NoSavedResult]: {
    id: "match.autoMatch.reason.noSavedResult",
    defaultMessage: "Noch kein gespeichertes Ergebnis, die Person steht bei den Kandidaten",
  },
  [MatchAutoRunReason.AlreadyInResult]: {
    id: "match.autoMatch.reason.alreadyInResult",
    defaultMessage: "Steht schon im Ergebnis",
  },
  [MatchAutoRunReason.OrganizationalRecordMismatch]: {
    id: "match.autoMatch.reason.organizationalRecordMismatch",
    defaultMessage: "Ergebnis und Bewertung sehen die Organisationsakte unterschiedlich",
  },
  [MatchAutoRunReason.ResultNotAppendable]: {
    id: "match.autoMatch.reason.resultNotAppendable",
    defaultMessage: "Das Ergebnis lässt sich nicht ergänzen",
  },
  [MatchAutoRunReason.ProfileNotReadable]: {
    id: "match.autoMatch.reason.profileNotReadable",
    defaultMessage: "Profil nicht lesbar",
  },
  [MatchAutoRunReason.RequesterWithoutAccess]: {
    id: "match.autoMatch.reason.requesterWithoutAccess",
    defaultMessage: "Wer eingeschaltet hat, sieht das Match nicht mehr oder darf die Bewerbungen der Ausschreibung nicht mehr lesen",
  },
  [MatchAutoRunReason.Error]: {
    id: "match.autoMatch.reason.error",
    defaultMessage: "Die Bewertung ist fehlgeschlagen",
  },
  [MatchAutoRunReason.DailyLimit]: {
    id: "match.autoMatch.reason.dailyLimit",
    defaultMessage: "Tageslimit erreicht, von Hand nachholen",
  },
};

export function runReasonLabel(reason: AutoRun["reason"]): MessageDescriptor | null {
  return reason ? (REASON_LABELS[reason] ?? null) : null;
}

/**
 * Ob "Neu matchen" bei einem Lauf mit geänderten Anforderungen noch etwas zu sagen hat. Ist seitdem ein
 * Ergebnis gespeichert worden, enthält es die Person schon (sie steht seit dem Lauf bei den Kandidaten).
 */
export function needsRematch(run: Pick<AutoRun, "status" | "createdAt" | "finishedAt">, latestSavedResultAt: string | null) {
  if (run.status !== MatchAutoRunStatus.DesignChanged) return false;
  if (!latestSavedResultAt) return true;
  return Date.parse(latestSavedResultAt) < Date.parse(run.finishedAt ?? run.createdAt);
}

/** Der Zustand einer Bewerbung, der eine angehängte Zeile überholt: zurückgezogen oder abgelehnt. */
export type OvertakingApplicationState = ListingApplicationState.Withdrawn | ListingApplicationState.Rejected;

export const OVERTAKING_STATE_LABELS: Record<OvertakingApplicationState, MessageDescriptor> = {
  [ListingApplicationState.Withdrawn]: WITHDRAWN,
  [ListingApplicationState.Rejected]: { id: "match.autoMatch.applicationRejected", defaultMessage: "Bewerbung abgelehnt" },
};

export interface AutoAddedMark {
  addedAt: string;
  applicationState: OvertakingApplicationState | null;
}

export interface AutoAddition {
  personId: string;
  addedAt: string;
  autoRunId?: string | null;
}

/**
 * Die Markierungen der angehängten Zeilen, je Person. Den Rückzug meldet der Lauf selbst (WITHDRAWN,
 * GENESIS-0073 §7); eine Absage ändert am Lauf nichts und ist nur aus der Bewerbung zu lesen, die nicht
 * jede Person sieht, die das Match sieht.
 */
export function autoAddedMarks(
  additions: readonly AutoAddition[],
  runs: readonly Pick<AutoRun, "id" | "status" | "applicationId">[],
  applicationStates: ReadonlyMap<string, ListingApplicationState>,
): Map<string, AutoAddedMark> {
  const runById = new Map(runs.map((run) => [run.id, run]));
  const marks = new Map<string, AutoAddedMark>();
  for (const addition of additions) {
    const run = addition.autoRunId ? runById.get(addition.autoRunId) : undefined;
    const state = run?.applicationId ? applicationStates.get(run.applicationId) : undefined;
    const withdrawn = run?.status === MatchAutoRunStatus.Withdrawn || state === ListingApplicationState.Withdrawn;
    marks.set(addition.personId, {
      addedAt: addition.addedAt,
      applicationState: withdrawn
        ? ListingApplicationState.Withdrawn
        : state === ListingApplicationState.Rejected
          ? ListingApplicationState.Rejected
          : null,
    });
  }
  return marks;
}

/** Die Bewerbungen hinter angehängten Zeilen, deren Zustand nur die Bewerbung selbst kennt. */
export function applicationIdsOf(
  additions: readonly AutoAddition[],
  runs: readonly Pick<AutoRun, "id" | "status" | "applicationId">[],
): string[] {
  const runById = new Map(runs.map((run) => [run.id, run]));
  const ids = new Set<string>();
  for (const addition of additions) {
    const run = addition.autoRunId ? runById.get(addition.autoRunId) : undefined;
    if (run?.applicationId && run.status !== MatchAutoRunStatus.Withdrawn) ids.add(run.applicationId);
  }
  return [...ids].sort();
}
